import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { useListUsersQuery } from '../redux/users/users.api';
import { useAddEventParticipantMutation } from '../redux/events/events.api';

interface Props {
  open: boolean;
  onClose: () => void;
  eventId: string;
  excludeUserIds?: string[];
}

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as { data?: { message?: string }; status?: number | string };
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error.';
  }
  return 'Failed to add';
};

const initial = (firstName?: string, lastName?: string): string =>
  (firstName || lastName || '?').charAt(0).toUpperCase();

export const AddParticipantModal = ({
  open,
  onClose,
  eventId,
  excludeUserIds = [],
}: Props): React.JSX.Element => {
  const excludeSet = React.useMemo(
    () => new Set(excludeUserIds),
    [excludeUserIds],
  );
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [addedUserIds, setAddedUserIds] = useState<Set<string>>(new Set());
  const [errorByUserId, setErrorByUserId] = useState<Record<string, string>>({});
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);

  // Reset state every time the modal opens so previous session doesn't leak.
  useEffect(() => {
    if (!open) return;
    setSearchInput('');
    setDebouncedSearch('');
    setAddedUserIds(new Set());
    setErrorByUserId({});
    setPendingUserId(null);
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isFetching } = useListUsersQuery(
    { search: debouncedSearch || undefined, page: 1, limit: 20 },
    { skip: !open },
  );

  const [addParticipant] = useAddEventParticipantMutation();

  const handleAdd = async (userId: string): Promise<void> => {
    setErrorByUserId((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    setPendingUserId(userId);
    try {
      await addParticipant({ eventId, userId }).unwrap();
      setAddedUserIds((prev) => {
        const next = new Set(prev);
        next.add(userId);
        return next;
      });
    } catch (err) {
      setErrorByUserId((prev) => ({
        ...prev,
        [userId]: extractErrorMessage(err),
      }));
    } finally {
      setPendingUserId(null);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add participant">
      <input
        className="admin-users__search add-participant__search"
        type="search"
        placeholder="Search by name, email, or username…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        autoFocus
      />
      <div className="add-participant__list">
        {!data && isFetching && (
          <p className="user-detail__empty">Searching…</p>
        )}
        {(() => {
          if (!data) return null;
          const visible = data.items.filter((u) => !excludeSet.has(u.id));
          if (visible.length === 0) {
            return (
              <p className="user-detail__empty">
                {debouncedSearch
                  ? `No users match "${debouncedSearch}".`
                  : 'No users found.'}
              </p>
            );
          }
          return visible.map((u) => {
            const fullName =
              [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
            const added = addedUserIds.has(u.id);
            const pending = pendingUserId === u.id;
            const err = errorByUserId[u.id];
            return (
              <div key={u.id} className="add-participant__row">
                <div className="add-participant__user">
                  {u.avatarUrl ? (
                    <img
                      className="event-detail__user-avatar"
                      src={u.avatarUrl}
                      alt=""
                    />
                  ) : (
                    <div className="event-detail__user-avatar event-detail__user-avatar--blank">
                      {initial(u.firstName, u.lastName)}
                    </div>
                  )}
                  <div className="add-participant__user-info">
                    <div className="add-participant__user-name">{fullName}</div>
                    <div className="add-participant__user-email">{u.email}</div>
                  </div>
                </div>
                <div className="add-participant__action">
                  {added ? (
                    <span className="add-participant__added">✓ Added</span>
                  ) : (
                    <button
                      type="button"
                      className="add-participant__add-btn"
                      onClick={() => handleAdd(u.id)}
                      disabled={pending}
                    >
                      {pending ? 'Adding…' : 'Add'}
                    </button>
                  )}
                  {err && (
                    <span className="add-participant__err">{err}</span>
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>
    </Modal>
  );
};
